package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.components.Picker
import dev.lingvo.site.components.PickerRow
import dev.lingvo.site.ds.IconSize
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LChevronLeftIcon
import dev.lingvo.site.ds.LIconAction
import dev.lingvo.site.ds.LProgressBar
import dev.lingvo.site.ds.LSearchIcon
import dev.lingvo.site.ds.LWandSparklesIcon
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.iconSize
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.trf
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** One row in the editor's locale picker. */
internal data class EditorLocaleOption(
    val code: String,
    val label: String,
    val path: String,
    val current: Boolean,
)

/**
 * The project's target languages, as picker rows — the source locale excluded, since there is
 * nothing to translate it into.
 *
 * The editor is the only surface whose working axis, the language, had no control: it was a URL
 * segment rendered as flat text, and moving from `de` to `fr` meant leaving the editor entirely,
 * going to Overview, and finding a readiness link. Names resolve through [localeInfo], which falls
 * back to the raw code for anything it does not recognise, so an unrecognised locale still labels
 * itself rather than showing blank.
 */
internal fun editorLocaleOptions(
    slug: String,
    sourceLocale: String,
    locales: List<String>,
    current: String,
): List<EditorLocaleOption> =
    locales.filter { it != sourceLocale }.map { locale ->
        EditorLocaleOption(
            code = locale,
            label = localeInfo(locale).englishName,
            path = "/app/projects/$slug/translate/$locale",
            current = locale == current,
        )
    }

@Composable
internal fun EditorHeader(
    state: TranslationEditorState,
    workspace: EditorWorkspaceState,
    onShowQueue: (EditorFocusRef?) -> Unit,
    onOpenUtility: (EditorUtilitySurface, EditorFocusRef) -> Unit,
    onOpenOverflow: (EditorFocusRef) -> Unit,
    onCloseOverflow: () -> Unit,
    onOpenModal: (EditorModalSurface, EditorFocusRef) -> Unit,
) {
    val stringsPath = "/app/projects/${state.projectSlug}/keys"
    val projectName = state.project?.name ?: state.projectSlug
    val projectTitle = "$projectName / ${localeInfo(state.locale).englishName}"
    val localeOptions = editorLocaleOptions(
        slug = state.projectSlug,
        sourceLocale = state.project?.sourceLocale.orEmpty(),
        locales = state.project?.locales.orEmpty(),
        current = state.locale,
    )
    val compactEditor = workspace.renderedLayout() == EditorRenderedLayout.COMPACT &&
        workspace.primaryView == EditorPrimaryView.EDITOR
    val compactTitle = state.selectedRow?.key?.name
        ?: t("editor.editor_header.choose_a_key", "Choose a key")
    val remaining = remainingTranslations(
        total = state.progress.total,
        translated = state.progress.translated,
        approved = state.progress.approved,
    )

    Row(
        EditorHeaderStyle.toModifier().attr("data-editor-header", "true"),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        if (compactEditor) {
            LButton(
                label = t("editor.editor_header.keys", "Keys"),
                onClick = {
                    onShowQueue(
                        state.selectedKeyId?.let(EditorFocusRef::QueueRow),
                    )
                },
                modifier = EditorCoarseTargetStyle.toModifier()
                    .attr("data-editor-coarse-target", "header"),
                variant = LButtonVariant.GHOST,
                size = LButtonSize.SM,
                leadingIcon = { LChevronLeftIcon() },
                accessibleLabel = t("editor.editor_header.back_to_keys", "Back to keys"),
            )
        } else {
            Link(
                path = stringsPath,
                modifier = EditorHeaderBackLinkStyle.toModifier()
                    .then(EditorCoarseTargetStyle.toModifier())
                    .attr("data-editor-coarse-target", "header")
                    .attr("aria-label", t("editor.editor_header.keys", "Keys")),
                variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
            ) {
                LChevronLeftIcon(Modifier.iconSize(IconSize.MD))
                Text(t("editor.editor_header.keys", "Keys"))
            }
        }

        when {
            compactEditor -> Span(
                EditorHeaderTitleStyle.toModifier()
                    .flexGrow(1)
                    .toAttrs { attr("title", compactTitle) },
            ) { Text(compactTitle) }

            // One target locale is not a choice — same reasoning as the trail's own
            // `WorkspaceCrumb`: drawing a disclosure for it is noise the picker exists to avoid.
            localeOptions.size < 2 -> Span(
                EditorHeaderTitleStyle.toModifier()
                    .flexGrow(1)
                    .toAttrs { attr("title", projectTitle) },
            ) { Text(projectTitle) }

            // The editor's working axis, the language, used to be flat text here — moving from
            // `de` to `fr` meant leaving the editor, going to Overview, and finding a readiness
            // link. This reuses AppTrail's Picker (real anchors, arrow-key navigation, Escape to
            // close) rather than growing a second popover.
            else -> Row(
                Modifier.flexGrow(1).gap(Space.XS).minWidth(0.px),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                // **The project name yields its characters before the locale does.** Both carry
                // `EditorHeaderTitleStyle`'s `flex: 1 1 auto`, so at 390 they shrank together and
                // the header read "Lingvo Mo… / Ger…" — the one fact that says which language this
                // screen edits cut to three letters, and "German" and "German (Austria)"
                // indistinguishable. Tripling this half's shrink factor spends the deficit here
                // first; the picker still shrinks after it, so the 320px header contract
                // (`EditorControllerGeometryTest`) keeps its escape.
                Span(
                    EditorHeaderTitleStyle.toModifier()
                        .styleModifier { property("flex", "1 3 auto") }
                        .toAttrs { attr("title", projectName) },
                ) { Text(projectName) }
                Span(Modifier.toAttrs { attr("aria-hidden", "true") }) { Text("/") }
                var localeOpen by remember { mutableStateOf(false) }
                val currentLocaleLabel = localeInfo(state.locale).englishName
                Picker(
                    label = currentLocaleLabel,
                    ariaLabel = trf(
                        "editor.editor_header.switch_language_currently",
                        "Switch language, currently {language}",
                        "language" to currentLocaleLabel,
                    ),
                    idSeed = "editor-locale-${state.locale}",
                    open = localeOpen,
                    onOpenChange = { localeOpen = it },
                    labelLang = state.locale,
                ) {
                    localeOptions.forEach { option ->
                        // Closing on activation is the popover's own job — its container closes on
                        // any click inside, since a row is a link and navigation follows. Nothing
                        // else needs to happen here, matching the trail's own static rows.
                        PickerRow(
                            label = option.label,
                            href = option.path,
                            current = option.current,
                            onActivate = {},
                            labelLang = option.code,
                        )
                    }
                }
            }
        }
        Spacer()
        val wideHeader = workspace.viewportWidth >= EDITOR_NARROW_HEADER_BREAKPOINT_PX
        if (
            wideHeader &&
            // **Nothing rather than a confident zero.** `progress.total` is 0 until the project
            // lands, so this rendered "0 remaining" on every load and then flipped to the real
            // figure — announcing, for a second or so, that a locale needing forty translations
            // needed none. A wrong number stated plainly is worse than an absent one: it is the only
            // thing in this header a reader can act on, and the load is exactly when they are
            // deciding whether to keep waiting.
            //
            // Guarded on `project` rather than `keysLoading` to match `CompactQueueHeader`, which
            // already returns its counting sentence from the same condition — one rule for the same
            // fact in both headers.
            state.project != null
        ) {
            // **The bar carries the shape, the numeral carries the fact.** The sentence here used
            // to be clamped by `text-overflow: ellipsis` inside a header that also holds a project
            // name and a locale picker — so on the widths where it was drawn at all it was often
            // drawn as "9 remain…". A compact bar cannot be clipped into a lie, and the count beside
            // it is four characters rather than a sentence.
            Row(
                EditorHeaderProgressStyle.toModifier(),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Span(EditorHeaderProgressBarStyle.toModifier().toAttrs()) {
                    LProgressBar(
                        progress = state.progress,
                        label = t("editor.editor_header.translation_progress", "Translation progress"),
                        compact = true,
                    )
                }
                Span(Modifier.toAttrs()) {
                    Text(
                        tPlural(
                            "editor.editor_header.remaining",
                            remaining,
                            "{count} remaining",
                            "{count} remaining",
                        ),
                    )
                }
            }
        }
        // **`LIconAction`, now that the fixtures can host it.** This was an `LIconButton` with a
        // native `title` and a note saying why: `LIconAction` composes Silk's `AdvancedTooltip`,
        // which calls `Deferred` at mount and throws without a `DeferringHost` — the generated
        // application installs one, the editor's browser fixtures did not, and adopting it here
        // turned every production-screen editor test red at mount. The fixtures install it now, so
        // the one explanation this control has is the styled tooltip rather than the operating
        // system's, and it carries the chord that focuses the same field.
        LIconAction(
            label = t("editor.editor_header.focus_key_search", "Focus key search"),
            onClick = { onShowQueue(EditorFocusRef.Search) },
            modifier = EditorCoarseTargetStyle.toModifier()
                .attr("data-editor-coarse-target", "header"),
            size = LButtonSize.SM,
            // The editor's own chord for this, read off `editorShortcutFor`: a bare "/" with no
            // modifier, because the editor is a full-screen surface where the queue list — not a
            // text field — is what has focus between keys.
            shortcut = "/",
        ) {
            LSearchIcon()
        }
        if (state.selectedRow != null) {
            Row(Modifier.gap(Space.XS), verticalAlignment = Alignment.CenterVertically) {
                val assistModifier = EditorCoarseTargetStyle.toModifier()
                    .attr("id", EDITOR_HEADER_ASSIST_TRIGGER_ID)
                    .attr("aria-controls", EDITOR_UTILITY_SURFACE_ID)
                    .attr("data-editor-coarse-target", "header")
                    .attr(
                        "aria-expanded",
                        (workspace.utility == EditorUtilitySurface.ASSIST ||
                            workspace.utility == EditorUtilitySurface.DISCUSS ||
                            workspace.utility == EditorUtilitySurface.ACTIVITY).toString(),
                    )
                val openAssist = {
                    onOpenUtility(
                        EditorUtilitySurface.ASSIST,
                        EditorFocusRef.ElementId(EDITOR_HEADER_ASSIST_TRIGGER_ID),
                    )
                }
                // **The sparkle now means exactly one thing.** It was on this trigger *and* on the
                // inline "Generate suggestion" in the source row, which are different promises: one
                // opens a panel, the other spends a provider call. The wand keeps the panel; the
                // sparkle keeps the generation.
                if (wideHeader) {
                    LButton(
                        label = t("editor.editor_header.assist", "Assist"),
                        onClick = openAssist,
                        modifier = assistModifier,
                        variant = LButtonVariant.SECONDARY,
                        size = LButtonSize.SM,
                        leadingIcon = { LWandSparklesIcon() },
                        accessibleLabel = t("editor.editor_header.open_assist", "Open Assist"),
                    )
                } else {
                    LIconAction(
                        label = t("editor.editor_header.open_assist", "Open Assist"),
                        onClick = openAssist,
                        modifier = assistModifier,
                        size = LButtonSize.SM,
                    ) {
                        LWandSparklesIcon()
                    }
                }
                val attentionCount = state.currentAssistAttentionCount
                if (attentionCount > 0) {
                    LBadge(
                        text = attentionCount.toString(),
                        modifier = Modifier.attr(
                            "aria-label",
                            trf(
                                "editor.editor_header.assist_items_need_attention",
                                "{count} Assist items need attention",
                                "count" to attentionCount.toString(),
                            ),
                        ),
                        variant = LBadgeVariant.WARNING,
                    )
                }
            }
        }
        EditorOverflowMenu(
            state = state,
            expanded = workspace.overflowOpen,
            onOpenOverflow = onOpenOverflow,
            onCloseOverflow = onCloseOverflow,
            onOpenUtility = onOpenUtility,
            onOpenModal = onOpenModal,
        )
    }
}
